import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { documentUrl, listYatraPdfs } from "@/lib/jain-yatra-pdf-api";
import {
  Download,
  FileText,
  MapPin,
  Bell,
  BookOpen,
  Map,
  QrCode,
  Award,
  Heart,
  Bike,
  UserCheck,
  Phone,
  Sparkles,
  Star,
  ChevronDown,
  ArrowRight,
  Landmark,
  Volume2,
  Check,
  HeartHandshake,
  Coffee,
  BookUser,
  Users,
  Clock,
  Tag,
} from "lucide-react";
import heroTemple from "@/assets/hero-temple.png";
import aboutTemple from "@/assets/about-temple.jpg";
import mandala from "@/assets/mandala.png";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { useEffect, useRef, useState } from "react";
import { useI18n, type DictKey, type Lang } from "@/lib/i18n";
import { getActiveCityInfo, type CityInfo } from "@/lib/city-api";
import { listTemples, type Temple } from "@/lib/temples-api";
import { listActiveWalks, type Walk } from "@/lib/walks-api";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Jain Gaurav Yatra — Together for Dharma, Seva & Unity" },
      {
        name: "description",
        content:
          "Stay connected with the Jain Gaurav Yatra across 31 Ahmedabad Derasars — updates, routes, events, seva and announcements in one app.",
      },
    ],
  }),
  component: Landing,
});

const FEATURE_KEYS: { icon: any; tk: DictKey; dk: DictKey }[] = [
  { icon: BookOpen, tk: "f1_title", dk: "f1_desc" },
  { icon: Map, tk: "f2_title", dk: "f2_desc" },
  { icon: QrCode, tk: "f3_title", dk: "f3_desc" },
  { icon: Award, tk: "f4_title", dk: "f4_desc" },
  { icon: Heart, tk: "f5_title", dk: "f5_desc" },
  { icon: Bike, tk: "f6_title", dk: "f6_desc" },
  { icon: UserCheck, tk: "f7_title", dk: "f7_desc" },
];

const STEP_KEYS: { n: string; tk: DictKey; dk: DictKey }[] = [
  { n: "01", tk: "s1_title", dk: "s1_desc" },
  { n: "02", tk: "s2_title", dk: "s2_desc" },
  { n: "03", tk: "s3_title", dk: "s3_desc" },
];

const BENEFIT_KEYS: DictKey[] = ["b1", "b2", "b3", "b4", "b5", "b6"];

const TESTIMONIAL_KEYS: { qk: DictKey; nk: DictKey; rk: DictKey }[] = [
  { qk: "t1_quote", nk: "t1_name", rk: "t1_role" },
  { qk: "t2_quote", nk: "t2_name", rk: "t2_role" },
  { qk: "t3_quote", nk: "t3_name", rk: "t3_role" },
];

const FAQ_KEYS: { qk: DictKey; ak: DictKey }[] = [
  { qk: "faq_q1", ak: "faq_a1" },
  { qk: "faq_q2", ak: "faq_a2" },
  { qk: "faq_q3", ak: "faq_a3" },
  { qk: "faq_q4", ak: "faq_a4" },
  { qk: "faq_q5", ak: "faq_a5" },
  { qk: "faq_q6", ak: "faq_a6" },
];

function Landing() {
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <Hero />
      <DiscoverCity />
      <SacredDerasars />
      <Walks />
      <About />
      <Features />
      <Steps />
      <AppPreview />
      <Benefits />
      <VolunteerCTA />
      <Banner />
      <Testimonials />
      <FaqSection />
      <FinalCTA />
      <SiteFooter />
    </div>
  );
}

function Hero() {
  const { t } = useI18n();
  const yatraPdf = useQuery({
    queryKey: ["jgy-yatra-pdfs", "public"],
    queryFn: ({ signal }) => listYatraPdfs(signal, true),
    retry: 1,
  });
  const pdfUrl =
    !yatraPdf.isError && yatraPdf.data?.[0]?.pdf ? documentUrl(yatraPdf.data[0].pdf) : null;
  return (
    <section className="relative overflow-hidden">
      <img
        src={mandala}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -left-32 top-10 w-[500px] opacity-25"
      />
      <img
        src={mandala}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -right-40 bottom-0 w-[600px] opacity-20"
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 py-10 sm:px-6 lg:grid-cols-2 lg:py-14">
        <div className="relative z-10">
          <span className="chip">{t("hero_chip")}</span>
          <h1 className="mt-4 font-serif text-5xl font-bold leading-[1.05] text-foreground sm:text-6xl lg:text-7xl">
            {t("hero_title_1")}
            <br />
            <span className="text-primary">{t("hero_title_2")}</span>
          </h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground">
            {t("hero_desc")}
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/login" className="btn-primary">
              <Download className="h-4 w-4" /> {t("hero_cta_primary")}
            </Link>
            <a href="#about" className="btn-outline">
              {t("hero_cta_secondary")}
            </a>
            {pdfUrl && (
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-outline">
                <FileText className="h-4 w-4" /> {t("hero_cta_pdf")}
              </a>
            )}
          </div>
        </div>
        <div className="relative">
          <div className="absolute -left-6 top-10 z-10 flex items-center gap-3 rounded-2xl bg-card px-4 py-3 shadow-[var(--shadow-soft)]">
            <span className="icon-tile h-9 w-9">
              <MapPin className="h-4 w-4" />
            </span>
            <div>
              <div className="text-sm font-semibold">{t("hero_badge_route")}</div>
              <div className="text-xs text-muted-foreground">{t("hero_badge_route_sub")}</div>
            </div>
          </div>
          <div className="absolute -right-4 bottom-12 z-10 flex items-center gap-3 rounded-2xl bg-card px-4 py-3 shadow-[var(--shadow-soft)]">
            <span className="icon-tile h-9 w-9">
              <Bell className="h-4 w-4" />
            </span>
            <div>
              <div className="text-sm font-semibold">{t("hero_badge_announce")}</div>
              <div className="text-xs text-muted-foreground">{t("hero_badge_announce_sub")}</div>
            </div>
          </div>
          <img
            src={heroTemple}
            alt="Ornate Jain temple in Ahmedabad"
            width={1280}
            height={960}
            className="aspect-[5/4] w-full rounded-3xl object-cover shadow-[var(--shadow-warm)]"
          />
        </div>
      </div>
    </section>
  );
}

/**
 * Minimal fetch-on-mount hook. The landing page deliberately avoids react-query: it is not a
 * dependency of this sub-project, so it resolves to the repo-root copy and pulls in a second React
 * instance, which breaks hooks when the app renders standalone. `load` must be a stable reference.
 */
function useAsyncList<T>(
  load: () => Promise<T[]>,
  initial: T[] = [],
): { data: T[]; loading: boolean } {
  const [data, setData] = useState<T[]>(initial);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    load()
      .then((rows) => {
        if (active) {
          setData(rows);
        }
      })
      .catch(() => {
        if (active) {
          setData([]);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [load]);

  return { data, loading };
}

function templeName(temple: Temple, lang: Lang): string {
  const name = lang === "hi" ? temple.name_hi : lang === "gu" ? temple.name_gu : temple.name_en;
  return name || temple.name_en;
}

/** Stable reference for useAsyncList. getActiveCityInfo never rejects. */
const loadCity = () => getActiveCityInfo().then((c) => (c ? [c] : []));

function DiscoverCity() {
  const { t } = useI18n();
  const [open, setOpen] = useState<number | null>(0);

  const { data, loading } = useAsyncList<CityInfo>(loadCity);
  const city = data[0];

  // Nothing to show (endpoint unreachable, or no active record) — drop the section entirely rather
  // than render an empty shell.
  if (!loading && !city) {
    return null;
  }

  const sections = city?.sections ?? [];
  const half = Math.ceil(sections.length / 2);
  const columns = [sections.slice(0, half), sections.slice(half)];

  return (
    <section
      id="city"
      className="bg-cream-deep py-10"
      style={{ background: "hsl(var(--cream-deep))" }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="chip">{t("city_chip")}</span>
          <h2 className="jgy-section-title mt-4">{city?.name || t("city_title")}</h2>
          <p className="jgy-section-lead mt-4">{city?.subtitle || t("city_desc")}</p>
        </div>

        {loading ? (
          <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-2xl border bg-card" />
            ))}
          </div>
        ) : (
          <>
            {city && city.quick_facts.length > 0 && (
              <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {city.quick_facts.map((fact, i) => (
                  <div key={i} className="rounded-2xl border bg-card p-4">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                      {fact.label}
                    </div>
                    <div className="mt-1 text-sm font-medium leading-snug text-foreground">
                      {fact.value}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5 grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
              {columns.map((col, colIndex) => (
                <div key={colIndex} className="space-y-5">
                  {col.map((section, i) => {
                    // Index within the flattened list, so only one panel is open across both columns.
                    const index = colIndex === 0 ? i : half + i;
                    const isOpen = open === index;
                    return (
                      <button
                        key={index}
                        onClick={() => setOpen(isOpen ? null : index)}
                        aria-expanded={isOpen}
                        className="block w-full rounded-2xl border bg-card p-5 text-left transition-all hover:shadow-[var(--shadow-soft)]"
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <span className="icon-tile h-9 w-9 shrink-0">
                              <Landmark className="h-4 w-4" />
                            </span>
                            <span className="font-serif text-base font-semibold text-foreground">
                              {section.title}
                            </span>
                          </div>
                          <ChevronDown
                            className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                          />
                        </div>
                        {isOpen && (
                          <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                            {section.content}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

const PREVIEW_COUNT = 8;

function SacredDerasars() {
  const { t, lang } = useI18n();
  const [showAll, setShowAll] = useState(false);

  const { data: temples, loading: isLoading } = useAsyncList(listTemples);

  const sorted = [...temples].sort((a, b) => a.yatra_order - b.yatra_order);
  const featured = showAll ? sorted : sorted.slice(0, PREVIEW_COUNT);
  const canExpand = sorted.length > PREVIEW_COUNT;

  return (
    <section className="py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="chip">{t("derasars_chip")}</span>
          <h2 className="jgy-section-title mt-4">
            {temples.length > 0 ? `${temples.length} ` : ""}
            {t("derasars_title")}
          </h2>
          <p className="jgy-section-lead mt-4">{t("derasars_desc")}</p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading
            ? [...Array(8)].map((_, i) => (
                <div key={i} className="overflow-hidden rounded-2xl border bg-card">
                  <div className="aspect-square w-full animate-pulse bg-muted" />
                  <div className="space-y-2 p-4">
                    <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
                  </div>
                </div>
              ))
            : featured.map((temple) => (
                <article
                  key={temple.id}
                  className="overflow-hidden rounded-2xl border bg-card transition-transform hover:-translate-y-1 hover:shadow-[var(--shadow-soft)]"
                >
                  <div className="relative">
                    {temple.image_url ? (
                      <img
                        src={temple.image_url}
                        alt={templeName(temple, lang)}
                        loading="lazy"
                        className="aspect-square w-full object-cover"
                      />
                    ) : (
                      <div
                        className="flex aspect-square w-full items-center justify-center text-primary-foreground"
                        style={{ background: "var(--gradient-saffron)" }}
                      >
                        <Landmark className="h-10 w-10 opacity-90" />
                      </div>
                    )}
                    <span className="absolute left-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-secondary-foreground text-xs font-bold text-secondary">
                      {temple.yatra_order}
                    </span>
                    {temple.audio_url ? (
                      <span
                        title={t("derasars_audio")}
                        className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-card/90 text-primary shadow-[var(--shadow-soft)]"
                      >
                        <Volume2 className="h-3.5 w-3.5" />
                      </span>
                    ) : null}
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-base font-semibold leading-tight text-foreground">
                      {templeName(temple, lang)}
                    </h3>
                    {temple.area ? (
                      <div className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" />
                        {temple.area}
                      </div>
                    ) : null}
                    {temple.mool_nayak ? (
                      <div className="mt-1.5 text-xs font-medium text-primary">
                        {t("moolNayak")} · {temple.mool_nayak}
                      </div>
                    ) : null}
                  </div>
                </article>
              ))}
        </div>

        {!isLoading && featured.length === 0 && (
          <p className="mt-8 text-center text-sm text-muted-foreground">{t("derasars_empty")}</p>
        )}

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {/* Expands the grid in place rather than navigating away. */}
          {canExpand ? (
            <button onClick={() => setShowAll((s) => !s)} className="btn-outline">
              {showAll
                ? t("derasars_show_less")
                : // "{n} " → live count, or drop the token while the list is still loading.
                  t("derasars_view_all").replace(
                    "{n} ",
                    sorted.length > 0 ? `${sorted.length} ` : "",
                  )}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${showAll ? "rotate-180" : ""}`}
              />
            </button>
          ) : null}
          <Link to="/login" className="btn-primary">
            {t("derasars_explore")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/** Stable reference for useAsyncList. listActiveWalks never rejects. */
const loadWalks = () => listActiveWalks();

const WALKS_PREVIEW = 3;

/** Picks an icon from the feature text, since the API stores features as free strings. */
function featureIcon(feature: string): typeof Check {
  const s = feature.toLowerCase();
  if (s.includes("breakfast") || s.includes("food") || s.includes("tea") || s.includes("chai")) {
    return Coffee;
  }
  if (s.includes("guide") || s.includes("volunteer")) {
    return BookUser;
  }
  if (s.includes("temple") || s.includes("derasar")) {
    return Landmark;
  }
  if (s.includes("group") || s.includes("family")) {
    return Users;
  }
  if (s.includes("hour") || s.includes("hr") || s.includes("min") || s.includes("time")) {
    return Clock;
  }
  if (s.includes("fee") || s.includes("free") || s.includes("price")) {
    return Tag;
  }
  return Check;
}

/** ₹1,200 for whole rupees; ₹0.50 for fractional — avoids rendering "₹0.5". */
function formatPrice(value: number): string {
  return value % 1 === 0 ? `₹${value.toLocaleString("en-IN")}` : `₹${value.toFixed(2)}`;
}

function Walks() {
  const { t } = useI18n();
  const [showAll, setShowAll] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);

  const { data: walks, loading } = useAsyncList<Walk>(loadWalks);

  // Nothing active (endpoint unreachable, or none published) — drop the section entirely.
  if (!loading && walks.length === 0) {
    return null;
  }

  const visible = showAll ? walks : walks.slice(0, WALKS_PREVIEW);
  const canExpand = walks.length > WALKS_PREVIEW;

  const toggle = () => {
    const collapsing = showAll;
    setShowAll(!collapsing);
    if (collapsing) {
      // Returning from a long list — bring the section header back into view.
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section
      id="walks"
      ref={sectionRef}
      className="scroll-mt-20 bg-cream-deep py-14"
      style={{ background: "hsl(var(--cream-deep))" }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="chip">{t("walks_chip")}</span>
          <h2 className="jgy-section-title mt-4">{t("walks_title")}</h2>
          <p className="jgy-section-lead mt-4">{t("walks_desc")}</p>
        </div>

        {loading ? (
          <div className="mx-auto mt-10 grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-96 animate-pulse rounded-3xl border bg-card" />
            ))}
          </div>
        ) : (
          <>
            {/* items-stretch keeps every card the same height regardless of content. */}
            <div className="mx-auto mt-10 grid max-w-6xl grid-cols-1 items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((w) => {
                return (
                  <article
                    key={w.id}
                    className="group relative flex flex-col rounded-3xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[var(--shadow-soft)]"
                  >
                    {/* Badges */}
                    <div className="flex flex-wrap gap-1.5">
                      {w.is_free ? (
                        <Pill tone="primary" icon={HeartHandshake}>
                          {t("walks_volunteer")}
                        </Pill>
                      ) : null}
                      {w.is_breakfast ? <Pill icon={Coffee}>{t("walks_breakfast")}</Pill> : null}
                      {w.is_guide ? <Pill icon={BookUser}>{t("walks_guide")}</Pill> : null}
                    </div>

                    <h3 className="mt-4 font-serif text-xl font-bold leading-snug text-foreground">
                      {w.title}
                    </h3>

                    {/* Price */}
                    <div className="mt-3 flex items-baseline gap-1.5">
                      <span className="font-serif text-4xl font-bold text-primary">
                        {w.is_free || w.price === 0 ? t("walks_free") : formatPrice(w.price)}
                      </span>
                      {w.is_free || w.price === 0 ? null : (
                        <span className="text-xs text-muted-foreground">
                          {t("walks_per_person")}
                        </span>
                      )}
                    </div>

                    {w.description ? (
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        {w.description}
                      </p>
                    ) : null}

                    {/* flex-1 pushes the CTA to the bottom so buttons align across cards. */}
                    <div className="mt-5 flex-1">
                      {w.features.length > 0 ? (
                        <>
                          <div className="mb-4 border-t border-dashed border-border" />
                          <ul className="space-y-2.5">
                            {w.features.map((f, i) => {
                              const Icon = featureIcon(f);
                              return (
                                <li
                                  key={i}
                                  className="flex items-start gap-2.5 text-sm text-foreground"
                                >
                                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                                    <Icon className="h-3 w-3" />
                                  </span>
                                  <span>{f}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </>
                      ) : null}
                    </div>

                    <Link to="/login" className="btn-primary mt-6 w-full justify-center">
                      {t("walks_book")}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </article>
                );
              })}
            </div>

            {canExpand ? (
              <div className="mt-10 flex justify-center">
                <button onClick={toggle} className="btn-outline">
                  {showAll ? t("walks_show_less") : t("walks_show_more")}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${showAll ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function Pill({
  children,
  icon: Icon,
  tone,
}: {
  children: React.ReactNode;
  icon: typeof Check;
  tone?: "primary";
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
        tone === "primary"
          ? "border-primary/25 bg-primary/10 text-primary"
          : "border-border bg-muted/60 text-muted-foreground"
      }`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {children}
    </span>
  );
}

function VolunteerCTA() {
  const { t } = useI18n();

  return (
    <section className="py-10">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="overflow-hidden rounded-3xl border bg-card p-8 text-center shadow-[var(--shadow-soft)] sm:p-10">
          <span className="icon-tile mx-auto">
            <HeartHandshake className="h-5 w-5" />
          </span>
          <span className="chip mt-4">{t("vol_chip")}</span>
          <h2 className="jgy-section-title mt-3">{t("vol_title")}</h2>
          <p className="jgy-section-lead mx-auto mt-4 max-w-2xl">{t("vol_desc")}</p>
          <Link to="/volunteer" className="btn-primary mx-auto mt-6">
            <HeartHandshake className="h-4 w-4" /> {t("vol_apply")}
          </Link>
        </div>
      </div>
    </section>
  );
}

function About() {
  const { t } = useI18n();
  const items: DictKey[] = ["about_b1", "about_b2", "about_b3", "about_b4"];
  return (
    <section id="about" className="relative py-10">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 sm:px-6 lg:grid-cols-2">
        <div className="relative">
          <img
            src={aboutTemple}
            alt="Jain temple at sunrise"
            width={1024}
            height={1024}
            loading="lazy"
            className="aspect-square w-full rounded-3xl object-cover shadow-[var(--shadow-soft)]"
          />
          <div className="absolute bottom-6 left-6 right-6 text-primary-foreground">
            <div className="text-xs font-medium opacity-90">{t("about_overlay_kicker")}</div>
            <div className="font-serif text-xl font-bold">{t("about_overlay_title")}</div>
          </div>
        </div>
        <div>
          <span className="chip">{t("about_chip")}</span>
          <h2 className="jgy-section-title mt-4">{t("about_title")}</h2>
          <p className="jgy-section-lead mt-5">{t("about_desc")}</p>
          <ul className="mt-5 space-y-4">
            {items.map((k) => (
              <li key={k} className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary">
                  <Heart className="h-3.5 w-3.5 text-primary-foreground" />
                </span>
                <span className="text-sm text-foreground">{t(k)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const { t } = useI18n();
  return (
    <section
      id="features"
      className="bg-cream-deep py-10"
      style={{ background: "hsl(var(--cream-deep))" }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="chip">{t("features_chip")}</span>
          <h2 className="jgy-section-title mt-4">{t("features_title")}</h2>
          <p className="jgy-section-lead mt-4">{t("features_desc")}</p>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURE_KEYS.map((f) => (
            <div key={f.tk} className="card-soft transition-transform hover:-translate-y-1">
              <span className="icon-tile">
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="jgy-card-title mt-5">{t(f.tk)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(f.dk)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Steps() {
  const { t } = useI18n();
  return (
    <section id="how" className="relative overflow-hidden py-10">
      <img
        src={mandala}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -right-40 top-1/2 w-[600px] -translate-y-1/2 opacity-25"
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="chip">{t("steps_chip")}</span>
          <h2 className="jgy-section-title mt-4">{t("steps_title")}</h2>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {STEP_KEYS.map((s) => (
            <div key={s.n} className="card-soft">
              <div className="font-serif text-4xl font-bold text-primary">{s.n}</div>
              <h3 className="jgy-card-title mt-3">{t(s.tk)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(s.dk)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AppPreview() {
  const { t } = useI18n();
  const screens: { tk: DictKey; body: any }[] = [
    { tk: "screen_home", body: <HomeScreen /> },
    { tk: "screen_directory", body: <DirectoryScreen /> },
    { tk: "screen_certificate", body: <CertScreen /> },
    { tk: "screen_bookings", body: <BookingsScreen /> },
    { tk: "screen_volunteer", body: <VolunteerScreen /> },
  ];
  return (
    <section className="bg-cream-deep py-10" style={{ background: "hsl(var(--cream-deep))" }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="chip">{t("preview_chip")}</span>
          <h2 className="jgy-section-title mt-4">{t("preview_title")}</h2>
          <p className="jgy-section-lead mt-4">{t("preview_desc")}</p>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-5">
          {screens.map((s) => (
            <div key={s.tk} className="flex flex-col items-center">
              <div
                className="aspect-[9/19] w-full overflow-hidden rounded-[2rem] border-4 p-2 shadow-[var(--shadow-warm)]"
                style={{ borderColor: "hsl(var(--saffron-deep))" }}
              >
                <div className="h-full w-full overflow-hidden rounded-[1.5rem] bg-card text-[10px]">
                  {s.body}
                </div>
              </div>
              <div className="mt-3 text-sm font-semibold text-foreground">{t(s.tk)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeScreen() {
  const { t } = useI18n();
  return (
    <div className="flex h-full flex-col gap-1.5 p-2 text-foreground">
      <div
        className="rounded-xl p-2 text-primary-foreground"
        style={{ background: "var(--gradient-saffron)" }}
      >
        <div className="text-[8px] opacity-90">{t("welcome")}</div>
        <div className="text-sm font-bold">Rajesh Shah</div>
        <div className="text-[8px] opacity-90">Navrangpura · Member #JGY-2041</div>
        <div className="mt-1.5 rounded-md bg-primary-foreground/20 p-1.5">
          <div className="flex items-center justify-between">
            <span>{t("progress")}</span>
            <span className="rounded-full bg-primary-foreground/30 px-1">12/31</span>
          </div>
          <div className="mt-1 h-1 rounded-full bg-primary-foreground/30">
            <div className="h-1 w-[39%] rounded-full bg-primary-foreground" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {[t("temples"), t("map"), "QR", t("certificate")].map((x, i) => (
          <div key={i} className="rounded bg-muted p-1.5 text-center text-[8px] font-semibold">
            {x}
          </div>
        ))}
      </div>
      <div className="rounded-lg border p-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[8px] font-bold uppercase text-primary">Today</span>
          <span className="text-[7px] text-muted-foreground">Yatra Day 4</span>
        </div>
        <div className="mt-0.5 text-[9px] font-semibold">Hutheesing Jain Temple</div>
        <div className="text-[7px] text-muted-foreground">Aarti 06:30 · Pravachan 18:00</div>
      </div>
      <div className="rounded-lg border bg-primary/10 p-1.5">
        <div className="text-[8px] font-bold text-primary">📢 Announcement</div>
        <div className="text-[7px] text-primary/90">
          Bus assembly at Paldi temple at 7:00 AM sharp.
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1 text-center">
        <div className="rounded bg-muted p-1">
          <div className="font-bold">12</div>
          <div className="text-[7px] text-muted-foreground">Visited</div>
        </div>
        <div className="rounded bg-muted p-1">
          <div className="font-bold">3</div>
          <div className="text-[7px] text-muted-foreground">Bookings</div>
        </div>
        <div className="rounded bg-muted p-1">
          <div className="font-bold">5</div>
          <div className="text-[7px] text-muted-foreground">Seva</div>
        </div>
      </div>
    </div>
  );
}
function DirectoryScreen() {
  const { t } = useI18n();
  const items = [
    { n: "Hutheesing Jain", a: "Shahibaug", s: "ok" },
    { n: "Mahavir Swami", a: "Paldi", s: "ok" },
    { n: "Parshwanath", a: "Naranpura", s: "pending" },
    { n: "Adinath", a: "Maninagar", s: "pending" },
    { n: "Shantinath", a: "Vastrapur", s: "pending" },
    { n: "Chintamani", a: "Saraspur", s: "pending" },
    { n: "Sambhavnath", a: "Ranip", s: "pending" },
    { n: "Vimalnath", a: "Bopal", s: "pending" },
  ];
  return (
    <div className="p-2">
      <div className="flex items-center justify-between">
        <div className="font-bold">{t("temples")}</div>
        <span className="text-[7px] text-muted-foreground">31 total</span>
      </div>
      <div className="mt-1 rounded bg-muted px-1 py-0.5 text-[7px] text-muted-foreground">
        🔍 Search by area or name…
      </div>
      <div className="mt-1 space-y-1">
        {items.map((x, i) => (
          <div key={x.n} className="flex items-center gap-1 rounded border p-1">
            <div
              className="flex h-6 w-6 items-center justify-center rounded text-[8px] font-bold text-primary-foreground"
              style={{ background: "var(--gradient-saffron)" }}
            >
              {i + 1}
            </div>
            <div className="flex-1">
              <div className="text-[9px] font-semibold leading-tight">{x.n}</div>
              <div className="text-[7px] text-muted-foreground">{x.a}</div>
            </div>
            <span
              className={`rounded-full border px-1 text-[7px] ${x.s === "ok" ? "border-primary/25 bg-primary/15 text-primary" : "bg-primary/12 text-primary"}`}
            >
              {x.s === "ok" ? "✓" : "•"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
function CertScreen() {
  const { t } = useI18n();
  return (
    <div className="flex h-full flex-col p-2 text-center">
      <div className="font-bold">{t("certificate")}</div>
      <div
        className="mt-1 flex-1 rounded border-2 border-dashed p-2"
        style={{ borderColor: "hsl(var(--saffron))" }}
      >
        <div className="text-[7px] tracking-widest opacity-70">SHRI</div>
        <div className="text-[8px] font-bold tracking-wider text-primary">JAIN GAURAV YATRA</div>
        <div className="mt-1 text-[7px] italic text-muted-foreground">This is to certify</div>
        <div className="mt-1 font-serif text-sm font-bold">Rajesh Shah</div>
        <div className="text-[7px] text-muted-foreground">has visited 12 of 31 derasars</div>
        <div
          className="mt-1 h-10 w-10 mx-auto rounded-full"
          style={{ background: "var(--gradient-saffron)" }}
        />
        <div className="mt-1 text-[7px] font-semibold">Shri Sangh · Ahmedabad</div>
        <div className="mt-1 grid grid-cols-2 gap-1 text-[7px]">
          <div className="rounded bg-muted p-0.5">Issued · 20-Nov-25</div>
          <div className="rounded bg-muted p-0.5">ID · JGY-2041</div>
        </div>
      </div>
      <div
        className="mt-1 rounded py-1 text-[8px] font-semibold text-primary-foreground"
        style={{ background: "var(--gradient-saffron)" }}
      >
        ⬇ Download PDF
      </div>
    </div>
  );
}
function BookingsScreen() {
  const { t } = useI18n();
  const list = [
    { n: "Ramesh Patel", t: "Rickshaw", d: "20-Nov · 08:00", s: "ok" },
    { n: "Kiran Jain", t: "Volunteer", d: "20-Nov · 10:30", s: "ok" },
    { n: "Suresh Kumar", t: "Rickshaw", d: "21-Nov · 07:00", s: "ok" },
    { n: "Manish Zaveri", t: "Volunteer", d: "21-Nov · 17:00", s: "pn" },
    { n: "Hitesh Solanki", t: "Rickshaw", d: "22-Nov · 06:30", s: "pn" },
    { n: "Anjali Shah", t: "Volunteer", d: "23-Nov · 09:00", s: "pn" },
  ];
  return (
    <div className="p-2">
      <div className="flex items-center justify-between">
        <div className="font-bold">{t("myBookings")}</div>
        <span className="text-[7px] rounded-full bg-muted px-1">6</span>
      </div>
      <div className="mt-1 space-y-1">
        {list.map((b) => (
          <div key={b.n} className="rounded border p-1">
            <div className="flex justify-between">
              <span className="text-[9px] font-semibold">{b.n}</span>
              <span
                className={`text-[7px] rounded-full border px-1 ${b.s === "ok" ? "border-primary/25 bg-primary/15 text-primary" : "bg-primary/12 text-primary"}`}
              >
                {b.s === "ok" ? "Confirmed" : "Pending"}
              </span>
            </div>
            <div className="flex items-center justify-between text-[7px] text-muted-foreground">
              <span>{b.t}</span>
              <span>{b.d}</span>
            </div>
          </div>
        ))}
      </div>
      <div
        className="mt-1 rounded py-1 text-center text-[8px] font-semibold text-primary-foreground"
        style={{ background: "var(--gradient-saffron)" }}
      >
        + New Booking
      </div>
    </div>
  );
}
function VolunteerScreen() {
  const { t } = useI18n();
  const vols = [
    { n: "Kiran Jain", l: "Gujarati, English", a: "Paldi", r: "★ 4.9" },
    { n: "Manish Zaveri", l: "Hindi, Gujarati", a: "Navrangpura", r: "★ 4.8" },
    { n: "Anjali Shah", l: "Gujarati, English", a: "Bopal", r: "★ 5.0" },
    { n: "Bhavesh Mehta", l: "Hindi, English", a: "Maninagar", r: "★ 4.7" },
    { n: "Pooja Doshi", l: "Gujarati, Hindi", a: "Vastrapur", r: "★ 4.9" },
  ];
  return (
    <div className="p-2">
      <div className="flex items-center justify-between">
        <div className="font-bold">{t("bookVolunteer")}</div>
        <span className="text-[7px] rounded-full bg-muted px-1">20+</span>
      </div>
      <div className="mt-1 space-y-1">
        {vols.map((v) => (
          <div key={v.n} className="rounded border p-1">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-semibold">{v.n}</span>
              <span className="text-[7px] text-primary">{v.r}</span>
            </div>
            <div className="text-[7px] text-muted-foreground">
              {v.l} · {v.a}
            </div>
            <div
              className="mt-0.5 rounded px-1 py-0.5 text-center text-[7px] text-primary-foreground"
              style={{ background: "var(--gradient-saffron)" }}
            >
              {t("bookNow")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Benefits() {
  const { t } = useI18n();
  return (
    <section className="py-10">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 sm:px-6 lg:grid-cols-2">
        <div>
          <span className="chip">{t("benefits_chip")}</span>
          <h2 className="jgy-section-title mt-4">{t("benefits_title")}</h2>
          <p className="jgy-section-lead mt-4">{t("benefits_desc")}</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {BENEFIT_KEYS.map((k, i) => (
            <div key={k} className="flex items-start gap-3 rounded-2xl border bg-card p-4">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                {i + 1}
              </span>
              <span className="text-sm leading-relaxed text-muted-foreground">{t(k)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Banner() {
  const { t } = useI18n();
  return (
    <section
      className="relative overflow-hidden py-10 text-center text-primary-foreground"
      style={{ background: "var(--gradient-saffron)" }}
    >
      <img src={mandala} alt="" aria-hidden className="absolute -left-20 top-0 h-full opacity-20" />
      <img
        src={mandala}
        alt=""
        aria-hidden
        className="absolute -right-20 top-0 h-full opacity-20"
      />
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6">
        <Sparkles className="mx-auto mb-4 h-6 w-6" />
        <h2 className="jgy-section-title-on-primary">{t("banner_title")}</h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-primary-foreground/95">
          {t("banner_desc")}
        </p>
      </div>
    </section>
  );
}

function Testimonials() {
  const { t } = useI18n();
  return (
    <section className="bg-cream-deep py-10" style={{ background: "hsl(var(--cream-deep))" }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="chip">{t("test_chip")}</span>
          <h2 className="jgy-section-title mt-4">{t("test_title")}</h2>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {TESTIMONIAL_KEYS.map((tt) => (
            <div key={tt.nk} className="card-soft">
              <div className="flex gap-0.5 text-gold">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-foreground">"{t(tt.qk)}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground">
                  {t(tt.nk)[0]}
                </div>
                <div>
                  <div className="text-sm font-semibold">{t(tt.nk)}</div>
                  <div className="text-xs text-muted-foreground">{t(tt.rk)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const { t } = useI18n();
  const [open, setOpen] = useState<number | null>(1);
  return (
    <section id="faq" className="py-10">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="text-center">
          <span className="chip">{t("faq_chip")}</span>
          <h2 className="jgy-section-title mt-4">{t("faq_title")}</h2>
        </div>
        <div className="mt-6 space-y-3">
          {FAQ_KEYS.map((f, i) => (
            <button
              key={f.qk}
              onClick={() => setOpen(open === i ? null : i)}
              className="block w-full rounded-2xl border bg-card p-5 text-left transition-all hover:shadow-[var(--shadow-soft)]"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="font-serif text-base font-semibold text-foreground sm:text-lg">
                  {t(f.qk)}
                </span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`}
                />
              </div>
              {open === i && (
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t(f.ak)}</p>
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { t } = useI18n();
  return (
    <section className="py-10 text-center">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <h2 className="jgy-section-title">{t("cta_title")}</h2>
        <p className="jgy-section-lead mx-auto mt-4 max-w-lg">{t("cta_desc")}</p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link to="/login" className="btn-primary">
            <Download className="h-4 w-4" /> {t("cta_open")}
          </Link>
          <a href="tel:+919825000000" className="btn-outline">
            <Phone className="h-4 w-4" /> {t("cta_contact")}
          </a>
        </div>
      </div>
    </section>
  );
}
